/**
 * dApp bootstrap for @tonado/core's artifact loaders.
 *
 * Call `bootstrapTonadoCore()` once at app startup, before any code path
 * that calls `createDeposit`, `generateProof`, or `generateMergeProof`.
 *
 * Internals:
 *   - Poseidon needs the circom-compiled witness calculator wasm + the
 *     generic `witness_calculator.js` builder. We bundle the builder as
 *     an ESM module in `./witnessCalculator.js` (a copy of circom's
 *     output, manually wrapped from CommonJS). The wasms are served as
 *     static assets from `/tonado-artifacts/poseidon-probe/`.
 *   - Proof generation needs the `withdraw.wasm` + `withdraw_final.zkey`
 *     pair for withdraws, plus the merkleUpdate counterparts for
 *     deposits. Same static-asset pattern.
 *
 * Static assets are copied into the dApp's public folder by
 * `apps/dapp/scripts/copy-artifacts.mjs`, which runs as a `prebuild` /
 * `predev` step. They are then served by Vite at runtime URLs.
 */
import { registerPoseidonArtifactLoader, registerProofArtifactLoader } from "@tonado/core";

// ESM-wrapped copy of circom's witness_calculator.js (the only diff vs.
// the upstream file is `module.exports = ` → `export default `).
// @ts-expect-error — the .js file ships its own (lack of) types.
import builderModule from "./witnessCalculator.js";

const ARTIFACT_ROOT = "/tonado-artifacts";

async function fetchBytes(url: string): Promise<Uint8Array> {
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`Failed to load artifact ${url}: HTTP ${resp.status} ${resp.statusText}`);
  }
  return new Uint8Array(await resp.arrayBuffer());
}

async function fetchJson<T>(url: string): Promise<T> {
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`Failed to load JSON ${url}: HTTP ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as T;
}

let bootstrapped = false;

/**
 * Idempotent. Safe to call from React's StrictMode (which fires effects
 * twice in dev).
 */
export function bootstrapTonadoCore(): void {
  if (bootstrapped) return;
  bootstrapped = true;

  // Cast the imported builder. circom's witness_calculator.js exposes one
  // async builder function: `(wasm) => Promise<Calculator>`.
  const buildCalculator = builderModule as unknown as (wasm: Uint8Array | ArrayBuffer) => Promise<{
    calculateWitness: (
      input: { inputs: bigint[] },
      sanityCheck: boolean
    ) => Promise<Array<bigint | string | number>>;
  }>;

  registerPoseidonArtifactLoader(async (probeName) => {
    const wasmFile = probeName === "poseidonProbe1" ? "poseidonProbe1.wasm" : "poseidonProbe.wasm";
    const wasm = await fetchBytes(`${ARTIFACT_ROOT}/poseidon-probe/${wasmFile}`);
    return { wasm, buildCalculator };
  });

  registerProofArtifactLoader("withdraw", {
    loadWasm: () => fetchBytes(`${ARTIFACT_ROOT}/withdraw.wasm`),
    loadZkey: () => fetchBytes(`${ARTIFACT_ROOT}/withdraw_final.zkey`),
    loadVkey: () => fetchJson(`${ARTIFACT_ROOT}/verification_key.json`),
  });

  registerProofArtifactLoader("merkleUpdate", {
    loadWasm: () => fetchBytes(`${ARTIFACT_ROOT}/merkleUpdate.wasm`),
    loadZkey: () => fetchBytes(`${ARTIFACT_ROOT}/merkleUpdate_final.zkey`),
    loadVkey: () => fetchJson(`${ARTIFACT_ROOT}/merkleUpdate_verification_key.json`),
  });
}
